import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { AppRole, SessionUser } from "@rn/contracts";
import { api, tokens } from "./api";

type AuthTokens = { accessToken: string; refreshToken: string };
type AuthResponse = AuthTokens & { user: SessionUser & { fullName: string | null } };

type AuthState = {
  user: SessionUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    fullName: string;
    companyName?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  /**
   * Entra numa empresa para suporte (dono da plataforma). Troca só o token de
   * acesso: o refresh continua sendo o da conta, então ao sair — ou quando a
   * sessão vencer — a renovação devolve a sessão normal.
   */
  enterSupport: (tenantId: string, reason?: string) => Promise<void>;
  exitSupport: () => Promise<void>;
  /** `super_admin` passa por qualquer verificação. */
  can: (...roles: AppRole[]) => boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const clear = useCallback(() => {
    tokens.clear();
    setUser(null);
    // Sem isto, os dados do usuário anterior continuam no cache e podem
    // aparecer para quem entrar em seguida no mesmo navegador.
    queryClient.clear();
  }, [queryClient]);

  // Restaura a sessão no boot. O access token pode ter expirado; o cliente
  // renova sozinho no primeiro 401.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!tokens.access && !tokens.refresh) {
        setLoading(false);
        return;
      }
      try {
        const me = await api.get<SessionUser>("/auth/me");
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clear]);

  // O cliente emite este evento quando a renovação falha — a sessão acabou de
  // verdade e a interface precisa saber, mesmo que nenhum componente tenha
  // pedido nada.
  useEffect(() => {
    const onUnauthenticated = () => clear();
    window.addEventListener("rn:unauthenticated", onUnauthenticated);
    return () => window.removeEventListener("rn:unauthenticated", onUnauthenticated);
  }, [clear]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,

      async login(email, password) {
        const result = await api.anonymous<AuthResponse>("/auth/login", { email, password });
        tokens.set(result.accessToken, result.refreshToken);
        setUser(result.user);
        // O `/auth/me` traz o que o token não carrega: se a pessoa é dona da
        // plataforma. Falhar aqui não desfaz o login.
        setUser(await api.get<SessionUser>("/auth/me").catch(() => result.user));
      },

      async register(input) {
        const result = await api.anonymous<AuthResponse>("/auth/register", input);
        tokens.set(result.accessToken, result.refreshToken);
        setUser(result.user);
      },

      async logout() {
        const refreshToken = tokens.refresh;
        // O logout no servidor revoga a família de tokens. Falhar aqui não pode
        // impedir a saída local — sessão presa no navegador é pior.
        if (refreshToken) {
          await api.anonymous("/auth/logout", { refreshToken }).catch(() => {});
        }
        clear();
      },

      async enterSupport(tenantId, reason) {
        const sessao = await api.post<{ accessToken: string }>(
          `/platform/tenants/${tenantId}/support`,
          { reason: reason || null },
        );
        tokens.set(sessao.accessToken, tokens.refresh ?? "");
        queryClient.clear();
        setUser(await api.get<SessionUser>("/auth/me"));
      },

      async exitSupport() {
        await api.post("/platform/support/end").catch(() => {});
        const refreshToken = tokens.refresh;
        if (refreshToken) {
          try {
            const novo = await api.anonymous<AuthTokens>("/auth/refresh", { refreshToken });
            tokens.set(novo.accessToken, novo.refreshToken);
          } catch {
            clear();
            return;
          }
        }
        queryClient.clear();
        setUser(await api.get<SessionUser>("/auth/me"));
      },

      async switchTenant(tenantId) {
        const result = await api.post<AuthTokens>("/auth/switch-tenant", { tenantId });
        tokens.set(result.accessToken, result.refreshToken);
        // Os papéis mudam junto com o tenant: releitura obrigatória.
        setUser(await api.get<SessionUser>("/auth/me"));
        queryClient.clear();
      },

      can(...roles) {
        if (!user) return false;
        if (user.roles.includes("super_admin")) return true;
        return roles.some((role) => user.roles.includes(role));
      },
    }),
    [user, loading, clear, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return context;
}
